import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api, BracketStage, Match, TeamRef, TimbaPrediction } from '../api/client';
import { Flag } from './TeamBadge';
import { teamName } from '../lib/flags';
import { useStore } from '../stores/store';
import { getIdentity, signData } from '../lib/identity';
import { formatKickoff } from '../lib/format';
import { timbaBracketSlots, SlotDisp } from '../lib/timbaBracket';

const LOCK_MS = 0; // se cierra al kickoff (arranque del partido)
const MAX_GOALS = 9; // tope por equipo (en la llave alcanza de sobra)
const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

// Slot de respaldo desde el ref del partido, mientras carga la cascada de la llave.
function fallbackSlot(ref: TeamRef): SlotDisp {
  return { team: ref.resolved, label: ref.label ?? ref.name, confirmed: ref.resolved !== null && !ref.projected };
}

function isOpen(kickoffUtc: string | null): boolean {
  if (!kickoffUtc) return false;
  return Date.now() < new Date(kickoffUtc).getTime() - LOCK_MS;
}

// Fila de un equipo: bandera + nombre y, al lado, el marcador en grande con [-]/[+].
function TeamScore({
  flag,
  name,
  value,
  onChange,
  disabled,
}: {
  flag: string | null;
  name: string;
  value: number;
  onChange: (n: number) => void;
  disabled: boolean;
}) {
  return (
    <TeamRow>
      <Flag team={flag} size={26} />
      <span className="nm">{name}</span>
      <Stepper>
        <button type="button" aria-label="menos" disabled={disabled || value <= 0} onClick={() => onChange(clamp(value - 1, 0, MAX_GOALS))}>
          −
        </button>
        <span className="num">{value}</span>
        <button type="button" aria-label="más" disabled={disabled || value >= MAX_GOALS} onClick={() => onChange(clamp(value + 1, 0, MAX_GOALS))}>
          +
        </button>
      </Stepper>
    </TeamRow>
  );
}

export function TimbaPick({ match }: { match: Match }) {
  const navigate = useNavigate();
  const nick = useStore((s) => s.nick);
  const setNick = useStore((s) => s.setNick);
  const pub = (() => {
    try {
      return getIdentity().pub;
    } catch {
      return '';
    }
  })();

  const num = match.num as number;

  const finished = match.status === 'finished' && match.score != null;
  const open = isOpen(match.kickoffUtc) && !finished;

  const [g1, setG1] = useState(0);
  const [g2, setG2] = useState(0);
  const [pen, setPen] = useState<number | null>(null);
  const [points, setPoints] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ base: number; booster: number; bonus: number } | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [predsRec, setPredsRec] = useState<Record<number, TimbaPrediction>>({});
  const [stages, setStages] = useState<BracketStage[]>([]);

  // Precargar mis pronósticos (este + el resto, para armar los rivales tentativos en cascada).
  useEffect(() => {
    if (!pub) {
      setLoaded(true);
      return;
    }
    let alive = true;
    api
      .timbaPredictions(pub)
      .then((d) => {
        if (!alive) return;
        const rec: Record<number, TimbaPrediction> = {};
        d.predictions.forEach((p) => (rec[p.num] = p));
        setPredsRec(rec);
        const mine = rec[num];
        if (mine) {
          setG1(mine.g1);
          setG2(mine.g2);
          setPen(mine.pen);
          if (mine.points != null) {
            setPoints(mine.points);
            setDetail({ base: mine.base ?? mine.points, booster: mine.booster ?? 1, bonus: mine.bonus ?? 0 });
          }
        }
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, [pub, num]);

  // La llave (para resolver rivales tentativos de las rondas siguientes).
  useEffect(() => {
    let alive = true;
    api.bracket().then((d) => alive && setStages(d.stages)).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const slots = useMemo(() => timbaBracketSlots(stages, predsRec), [stages, predsRec]);
  const [s1, s2] = slots.get(num) ?? [fallbackSlot(match.team1), fallbackSlot(match.team2)];

  const draw = g1 === g2;
  const editable = open && loaded;
  // Hora real en que cierra la edición = kickoff − 2 h (no el kickoff).
  const closeIso = match.kickoffUtc ? new Date(new Date(match.kickoffUtc).getTime() - LOCK_MS).toISOString() : null;
  // En un empate hay que definir quién gana los penales (es parte del resultado final de la llave).
  const canSave = editable && (!draw || pen === 1 || pen === 2);

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setMsg('');
    const penToSend = draw ? pen : null;
    try {
      const res = await api.timbaPredict({
        pub,
        nick,
        num,
        g1,
        g2,
        pen: penToSend,
        sig: signData({ num, g1, g2, pen: penToSend }),
      });
      // Si el nombre elegido ya estaba en uso, el backend asignó uno con sufijo: lo adoptamos.
      const renamed = !!res.nick && res.nick !== nick;
      if (renamed) setNick(res.nick!);
      setMsg(renamed ? `Ese nombre ya estaba en uso → ahora sos "${res.nick}". ¡Pronóstico guardado!` : '¡Pronóstico guardado!');
      // Volver automáticamente a la vista de llaves; el mensaje alcanza a verse antes.
      setTimeout(() => navigate('/timba'), renamed ? 2000 : 700);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const t1Flag = s1.team;
  const t2Flag = s2.team;
  const t1Name = s1.team ? teamName(s1.team) : s1.label || 'Por definir';
  const t2Name = s2.team ? teamName(s2.team) : s2.label || 'Por definir';
  const tentative = (!!s1.team && !s1.confirmed) || (!!s2.team && !s2.confirmed);

  return (
    <Card>
      <h3>🎲 Tu pronóstico</h3>

      {finished && match.score && (
        <p className="result">
          Resultado: <b>{match.score.ft[0]}–{match.score.ft[1]}</b>
          {match.score.pen && <> · penales {match.score.pen[0]}–{match.score.pen[1]}</>}
        </p>
      )}

      {!finished && tentative && (
        <p className="tent">⚠️ Rivales tentativos (la llave todavía puede cambiar). Igual podés dejar tu pronóstico.</p>
      )}

      <Score>
        <TeamScore flag={t1Flag} name={t1Name} value={g1} onChange={setG1} disabled={!editable} />
        <TeamScore flag={t2Flag} name={t2Name} value={g2} onChange={setG2} disabled={!editable} />
      </Score>

      {draw && (
        <Pens>
          <div className="q">Empate a los 90' → ¿quién gana los penales?</div>
          <div className="opts">
            <button type="button" className={pen === 1 ? 'on' : ''} disabled={!editable} onClick={() => setPen(1)}>
              <Flag team={t1Flag} size={18} /> {t1Name}
            </button>
            <button type="button" className={pen === 2 ? 'on' : ''} disabled={!editable} onClick={() => setPen(2)}>
              <Flag team={t2Flag} size={18} /> {t2Name}
            </button>
          </div>
        </Pens>
      )}

      {finished ? (
        points != null && detail ? (
          <Breakdown>
            <div className="line">
              <span>Marcador</span>
              <span>
                <b>{detail.base}</b>
                {detail.booster > 1 && <> × <em>{detail.booster}</em> = <b>{detail.base * detail.booster}</b></>}
              </span>
            </div>
            {detail.bonus > 0 && (
              <div className="line">
                <span>🔑 Bonus de llave</span>
                <span><b>+{detail.bonus}</b></span>
              </div>
            )}
            <div className="total">
              <span>Total</span>
              <span><b>{points}</b> {points === 1 ? 'punto' : 'puntos'}</span>
            </div>
          </Breakdown>
        ) : (
          <p className="info">El partido ya terminó.</p>
        )
      ) : open ? (
        <>
          <SaveBtn type="button" onClick={save} disabled={!canSave || saving}>
            {saving ? 'Guardando…' : 'Guardar pronóstico'}
          </SaveBtn>
          {draw && pen == null && <p className="info">Elegí quién gana los penales para poder guardar.</p>}
          <p className="info">Editable hasta el arranque del partido · cierra el {formatKickoff(closeIso)}</p>
        </>
      ) : (
        <p className="info">Los pronósticos de este partido están cerrados (faltan menos de 2 h para el inicio).</p>
      )}

      {msg && <p className="msg">{msg}</p>}
    </Card>
  );
}

const Card = styled.div`
  background: linear-gradient(165deg, #1a1340, ${({ theme }) => theme.colors.surface} 70%);
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius};
  padding: 16px;
  margin-top: 14px;
  h3 { margin: 0 0 10px; font-size: 1.15rem; }
  .info { margin: 10px 0 0; font-size: 0.82rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .tent { margin: 0 0 10px; font-size: 0.82rem; line-height: 1.4; color: ${({ theme }) => theme.colors.boost}; }
  .result { margin: 0 0 8px; font-size: 0.95rem; }
  .msg { margin: 8px 0 0; font-size: 0.9rem; font-weight: 700; color: ${({ theme }) => theme.colors.accent}; }
`;

const Score = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TeamRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 8px 12px;
  .nm {
    flex: 1;
    font-weight: 700;
    font-size: 0.95rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Stepper = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  button {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.surfaceAlt};
    color: ${({ theme }) => theme.colors.text};
    font-size: 1.4rem;
    font-weight: 800;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  button:disabled { opacity: 0.35; }
  .num {
    min-width: 32px;
    text-align: center;
    font-size: 2.4rem;
    font-weight: 900;
    line-height: 1;
  }
`;

const Pens = styled.div`
  margin-top: 12px;
  .q { font-size: 0.85rem; font-weight: 700; margin-bottom: 8px; }
  .q .opt { color: ${({ theme }) => theme.colors.textMuted}; font-weight: 500; }
  .opts { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .opts button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
    font-weight: 700;
    font-size: 0.86rem;
  }
  .opts button.on { background: ${({ theme }) => theme.colors.accent}; color: ${({ theme }) => theme.colors.accentText}; border-color: ${({ theme }) => theme.colors.accent}; }
  .opts button:disabled { opacity: 0.55; }
`;

const Breakdown = styled.div`
  margin-top: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  overflow: hidden;
  .line, .total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 9px 12px;
    font-size: 0.9rem;
  }
  .line { border-bottom: 1px solid ${({ theme }) => theme.colors.border}; color: ${({ theme }) => theme.colors.textMuted}; }
  .line b { color: ${({ theme }) => theme.colors.text}; }
  .line em { font-style: normal; font-weight: 900; color: ${({ theme }) => theme.colors.boost}; }
  .total { background: ${({ theme }) => theme.colors.surfaceAlt}; font-weight: 700; }
  .total b { font-size: 1.25rem; color: ${({ theme }) => theme.colors.accent}; }
`;

const SaveBtn = styled.button`
  width: 100%;
  margin-top: 14px;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.accentText};
  border: none;
  border-radius: 999px;
  padding: 13px;
  font-weight: 800;
  font-size: 1rem;
  &:disabled { opacity: 0.5; }
`;
